import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { CiFaceSmile, CiImageOn, CiUser } from "react-icons/ci";
import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import { UpLoadBox, ColourPicker, ZoomImageUpload } from "..";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import { Rounded } from "../../assets/images";
import { convertToCssColor } from "../../utils/formatColorCode";
import { useAutoAdjustTextareaRows } from "../../utils/adjustScroll";

type CreateProps = {
  post: string;
  imageUpload: string[];
  onChange: boolean;
  handleOnChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: any;
  imageUploading: boolean;
  setOpenCreate: React.Dispatch<React.SetStateAction<boolean>>;
  setImageUpload: React.Dispatch<React.SetStateAction<string[]>>;
  setPost: React.Dispatch<React.SetStateAction<string>>;
  setOnChange: React.Dispatch<React.SetStateAction<boolean>>;
  setImages: React.Dispatch<React.SetStateAction<File[]>>;
  onVideoSelectFile: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  selectedVideos: string[];
  setSelectedVideos: React.Dispatch<React.SetStateAction<string[]>>;
  setCompressedVideos: (value: React.SetStateAction<File[]>) => void;
  colors: string[];
  setSelectedColor: React.Dispatch<React.SetStateAction<string | null>>;
  selectedColor: string | null;
  loading: boolean;
  _pImage?: string;
  images: any;
  toggle: boolean | (() => void);
  isVideoCompressing: boolean;
  compressedVideos: File[];
  thumbnail: any;
  setThumbnail: React.Dispatch<React.SetStateAction<string[]>>;
  selectedPrevColor: string | null;
  setSelectedPrevColor: (value: React.SetStateAction<string | null>) => void;
  UserName: string
};
const Create: React.FC<CreateProps> = ({
  setImageUpload,
  post,
  onChange,
  handleImageChange,
  handleOnChange,
  handleSubmit,
  imageUpload,
  imageUploading,
  setOpenCreate,
  setPost,
  setOnChange,
  setImages,
  onVideoSelectFile,
  selectedVideos,
  setSelectedVideos,
  setCompressedVideos,
  colors,
  setSelectedColor,
  selectedColor,
  loading,
  _pImage,
  images,
  toggle,
  isVideoCompressing,
  compressedVideos,
  thumbnail,
  setThumbnail,
  selectedPrevColor,
  setSelectedPrevColor,
  UserName,
}) => {
  const [openUpload, setOpenUpload] = React.useState(false);
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [disableButton, setDisableButton] = useState(false);

  const [openColorPicker, setColorPicker] = React.useState(false);

  const handleSelectColor = (color: string | null) => {
    setSelectedColor(color);
    setSelectedPrevColor(color);
  };

  const totalImages = imageUpload?.length || 0;
  const totalVideos = selectedVideos.length || 0;

  const fileInputRef = useRef<HTMLInputElement>(null);

  //This is used to target the file input when the icon is clicked.
  const handleIconClick = () => {
    fileInputRef.current!.click();
  };

  const onClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.isCustom ? emojiData.unified : emojiData.emoji;
    setPost((inputValue) => inputValue + emoji);
    if (emoji) {
      setOnChange(true);
    }
  };

  const cssFormatColor = selectedColor && convertToCssColor(selectedColor);

  const removeColor = () => {
    if (post.length >= 131 && selectedColor) {
      setSelectedColor(null);
      setColorPicker(false);
    } else if (post.length <= 130 && selectedPrevColor !== null) {
      setColorPicker(true);
      setSelectedColor(selectedPrevColor);
    }
  };

  useEffect(() => {
    removeColor();

    window.addEventListener("click", removeColor);

    return () => {
      window.removeEventListener("click", removeColor);
    };
  });
  const handleCloseImage = () => {
    setOpenUpload(false);
    setSelectedVideos([]);
    setImageUpload([]);
    setImages([]);
    setCompressedVideos([]);
    setThumbnail([]);
    setOnChange(false);
    setSelectedPrevColor(null);
  };

  const handleCloseCreate = () => {
    setPost("");
    setOpenCreate(false);
    setSelectedColor(null);
    setSelectedVideos([]);
    setImageUpload([]);
    setImages([]);
    setCompressedVideos([]);
    setThumbnail([]);
    setOnChange(false);
    setSelectedPrevColor(null);
    if (typeof toggle === "function") {
      toggle();
    }
  };
  // console.log(user.id);

  const allMediaUrls: string[] = [];

  // Convert selected videos to URLs
  selectedVideos.forEach((videoFile) => {
    if (videoFile) {
      // console.log(videoFile,"videoFile");
      allMediaUrls.push(videoFile);
    }
  });

  // Convert uploaded images to URLs
  imageUpload.forEach((media) => {
    if (media) {
      allMediaUrls.push(media);
    }
  });

  // console.log(allMediaUrls);
  const totalMedias = allMediaUrls.length || 0;

  const [showAllImages, setShowAllImages] = React.useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const handleClick = (index: number) => {
    setShowAllImages(index);
    setCurrentIndex(index);
    setDisableButton(false);
  };
  const handlePrev = () => {
    setDisableButton(false);
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setDisableButton(false);
    setCurrentIndex((prevIndex) =>
      prevIndex < totalMedias - 1 ? prevIndex + 1 : prevIndex
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const removeMedia = (index: number) => {
    const updatedImages = [...images];
    const updatedPreviews = [...imageUpload];

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);

    setImages(updatedImages);
    setImageUpload(updatedPreviews);
    if (updatedPreviews.length > 1) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  useAutoAdjustTextareaRows("scrollingText");

  const removeVideo = (index: number) => {
    // const updatedSelectedVideos = [...selectedVideos];
    const updatedCompressedVideos = [...compressedVideos];

    updatedCompressedVideos.splice(index, 1);
    // updatedSelectedVideos.splice(index, 1);

    setSelectedVideos((updatedSelectedVideos) =>
      updatedSelectedVideos.filter(
        (_, indexToDelete) => indexToDelete !== index
      )
    );
    setCompressedVideos(updatedCompressedVideos);
    if (updatedCompressedVideos.length > 1) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  return (
    <div className="fixed picModal inset-0 z-30 flex items-center justify-center backdrop-filter backdrop-blur-[10px] bg-[#FFFFFFC2] overflow-y-hidden">
      <div className=" flex flex-col rounded-lg lg:w-[32%] shadow-lg bg-white">
        <div className="flex justify-between h-[10%] items-center border-b border-b-[#D8D3D3] pr-7 py-1">
          <div className="flex items-center flex-1 text-xl font-Poppins font-medium justify-center text-[#000]">
            Create
          </div>
          <button
            className="py-2 px-2
            rounded-full text-[#33363F]
            bg-[#ECE6E6] active:bg-bgGrey
            hover:text-gray-600"
          >
            <AiOutlineClose size={20} onClick={handleCloseCreate} />
          </button>
        </div>
        <div className="px-[29px] flex flex-col pt-3 pb-8">
          <div className="">
            <div className="flex w-[70%] gap-2 mb-2 font-Poppins">
              {_pImage ? (
                <img
                  alt=""
                  src={ipfsGatewayImage(_pImage!)}
                  className="lg:h-11 lg:w-11 w-[30px] h-[30px] rounded-full"
                />
              ) : (
                <div className="lg:h-11 lg:w-11 w-[30px] h-[30px] bg-slate-100 flex items-center justify-center rounded-full">
                  <CiUser size={20} />
                </div>
              )}
              <div className="flex h-9 flex-col w-[70%]">
                <div className="text-[#222] font-medium text-[13.5px]">
                  {UserName}
                </div>
                <div className="rounded-sm bg-[rgba(33,87,89,0.005)] w-[35%] h-[19.24px] flex justify-center items-center lg:text-[11.2px] text-[8px] text-midGreen">
                  {/* {!community ? `${communityFeed?.community?.name} COMMUNITY` : communityFeed?.creator?.roles! } */}
                  Public
                </div>
              </div>
            </div>
          </div>
          {allMediaUrls && showAllImages !== null && totalMedias > 0 && (
            <ZoomImageUpload
              setShowAllImages={setShowAllImages}
              handleNext={handleNext}
              handlePrev={handlePrev}
              totalImages={totalMedias}
              image={allMediaUrls}
              currentIndex={currentIndex}
              selectedVideos={selectedVideos}
              imageUpload={imageUpload}
              removeMedia={removeMedia}
              removeMediaItem={removeVideo}
            />
          )}
          <form className="w-full relative " onSubmit={handleSubmit}>
            <textarea
              placeholder={`What’s on your mind, ${UserName}?`}
              value={post}
              onChange={handleOnChange}
              id="scrollingText"
              className={`resize-none pr-5 mb-3 w-full outline-none rounded p-3 block box-border font-SegoeUI placeholder:text-[19.3px] text-pretty placeholder:font-normal placeholder:text-[#777272] placeholder:font-SegoeUI j disabled:overflow-y-hidden disabled:bg-blend-overlay ${
                selectedColor
                  ? " text-white text-center placeholder:text-center text-3xl placeholder:text-3xl placeholder:text-white pt-28 pb-10 aspect-video"
                  : "text-black bg-white"
              } ${post.length > 130 ? "text-base" : "text-2xl"} `}
              style={{ backgroundColor: cssFormatColor! }}
              cols={40}
              color={selectedColor || ""}
              required={
                post.trim().length < 0 ||
                imageUpload.length < 0 ||
                selectedVideos.length < 0
              }
            />

            {isVideoCompressing && (
              <div className="fixed inset-0 z-30 flex items-center justify-center backdrop-filter backdrop-blur-[10px] bg-[#FFFFFFC2]">
                <img src={Rounded} alt="" className="h-10 w-10 animate-spin" />
              </div>
            )}
            {openUpload && (
              <UpLoadBox
                selectedVideos={selectedVideos}
                totalVideos={totalVideos}
                imageUpload={imageUpload}
                totalImages={totalImages}
                handleIconClick={handleIconClick}
                handleImageChange={handleImageChange}
                onVideoSelectFile={onVideoSelectFile}
                fileInputRef={fileInputRef}
                handleClose={handleCloseImage}
                handleClick={handleClick}
                setShowAllImages={setShowAllImages}
                isVideoCompressing={isVideoCompressing}
                showAllMedia={showAllImages}
                allMediaUrls={allMediaUrls}
                thumbnail={thumbnail}
              />
            )}

            <ColourPicker
              openColorPicker={openColorPicker}
              openUpload={openUpload}
              post={post}
              setColorPicker={setColorPicker}
              handleSelectColor={handleSelectColor}
              colors={colors}
            />

            <div className="w-full h-[41px] pr-11 bg-[#F6F6F6] rounded-[10px] mt-4 flex items-center justify-between pl-[18px] mb-4">
              <h1 className="text-[#777272] font-SegoeUI text-xs font-normal">
                Add to your post
              </h1>
              <div className="flex gap-3">
                <CiImageOn
                  size={24}
                  onClick={() => setOpenUpload(true)}
                  className="cursor-pointer"
                />

                <CiFaceSmile
                  size={24}
                  className="cursor-pointer active:fill-red-200"
                  onClick={() => setOpenEmojiPicker(!openEmojiPicker)}
                />
              </div>
              {openEmojiPicker && (
                <div
                  className="absolute bottom-28 right-0 ease-in-out duration-75 w-[60%] rounded text-[9px] font-Poppins z-20"
                  ref={containerRef}
                >
                  <EmojiPicker
                    onEmojiClick={onClick}
                    height={300}
                    theme={Theme.AUTO}
                    previewConfig={{
                      showPreview: false,
                    }}
                  />
                </div>
              )}
            </div>

            <button
              disabled={(loading || !post || !imageUpload) && disableButton}
              type="submit"
              className={`w-full h-[41px] flex items-center justify-center bg-[#D7D7D7] rounded-[10px] text-base font-Poppins font-medium text-white ${
                onChange
                  ? `bg-midGreen cursor-pointer ${
                      loading && "cursor-not-allowed bg-[#D7D7D7]"
                    }`
                  : "cursor-not-allowed"
              }`}
            >
              {loading ? "Posting..." : "Post"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Create;
